import React, { useEffect, useCallback, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Radio from "@mui/material/Radio";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

const MOTOR_TYPE_OPTIONS = [
  { label: "linePrimeFan.acMotor", value: "ZAmotbasic IE3" },
  { label: "linePrimeFan.ecMotor", value: "ECblue" },
];

const FAN_TYPE_OPTIONS = [
  { label: "linePrimeFan.ebmFan", value: "EBM Papst", disabled: true },
  { label: "linePrimeFan.ZiehlAbeggFan", value: "Ziehl-Abegg" },
];

const FAN_TABLE_COLUMNS = [
  { keys: ["type"], label: "Type" },
  { keys: ["ident"], label: "Ident" },
  { keys: ["quantity"], label: "Qty.", alwaysVisible: true },
  { keys: ["powerInput"], label: "Power input [kW]" },
  { keys: ["rpm", "rotationSpeed"], label: "RPM [1/min]" },
  { keys: ["efficiency"], label: "Efficiency [%]" },
  { keys: ["nominalPower", "power"], label: "Nominal power [kW]" },
  { keys: ["soundPower"], label: "Suction/Pressure dB(A)" },
];

function LinePrimeFan() {
  const { control, watch, setValue } = useFormContext();
  const { t } = useTranslation();

  const linePrimeFanType = watch("linePrimeFanType");
  const linePrimeMotorType = watch("linePrimeMotorType");
  const linePrimeFanTableData = watch("linePrimeFanTableData");
  const linePrimeSelectedFan = watch("linePrimeSelectedFan");
  const linePrimeFanCalculated = watch("linePrimeFanCalculated");
  const linePrimeFanCalculating = watch("linePrimeFanCalculating");
  const fanChartImage = watch("fanChartImage");
  const fanRows = Array.isArray(linePrimeFanTableData) ? linePrimeFanTableData : [];
  const hasFanPreview = linePrimeFanCalculated && !!fanChartImage;

  // Backend sends raw Base64 string
  const fanImageSrc = fanChartImage
    ? fanChartImage.startsWith("data:")
      ? fanChartImage
      : `data:image/png;base64,${fanChartImage}`
    : null;

  // Track previous fan type and motor type to detect changes
  const prevFanTypeRef = useRef(linePrimeFanType);
  const prevMotorTypeRef = useRef(linePrimeMotorType);

  // Invalidation: when fan type changes, clear motor type and all downstream fields
  useEffect(() => {
    if (
      prevFanTypeRef.current !== linePrimeFanType &&
      prevFanTypeRef.current !== undefined
    ) {
      setValue("linePrimeMotorType", "", { shouldValidate: false });
      setValue("linePrimeSelectedFan", null, { shouldValidate: false });
      setValue("linePrimeFanCalculated", false, { shouldValidate: false });
      setValue("fanChartImage", null, { shouldValidate: false });
      setValue("linePrimeFanTableData", null, { shouldValidate: false });
    }
    prevFanTypeRef.current = linePrimeFanType;
  }, [linePrimeFanType, setValue]);

  // Invalidation: when motor type changes, clear downstream fields
  useEffect(() => {
    if (
      prevMotorTypeRef.current !== linePrimeMotorType &&
      prevMotorTypeRef.current !== undefined
    ) {
      setValue("linePrimeSelectedFan", null, { shouldValidate: false });
      setValue("linePrimeFanCalculated", false, { shouldValidate: false });
      setValue("fanChartImage", null, { shouldValidate: false });
      setValue("linePrimeFanTableData", null, { shouldValidate: false });
    }
    prevMotorTypeRef.current = linePrimeMotorType;
  }, [linePrimeMotorType, setValue]);

  const handleFanSelect = useCallback((fan, fieldOnChange) => {
    fieldOnChange(fan);
    setValue("linePrimeFanCalculateRequested", true, { shouldValidate: false });
  }, [setValue]);

  const visibleColumns = FAN_TABLE_COLUMNS.filter((column) =>
    column.alwaysVisible ||
    fanRows.some((fan) =>
      column.keys.some((key) => fan?.[key] != null && fan[key] !== ""),
    ),
  );

  const getFanSignature = (fan) => {
    if (!fan || typeof fan !== "object") {
      return String(fan ?? "");
    }

    return JSON.stringify(fan);
  };

  const getFanColumnValue = (fan, column) => {
    const matchingKey = column.keys.find(
      (key) => fan?.[key] != null && fan[key] !== "",
    );

    return matchingKey ? String(fan[matchingKey]) : "";
  };

  // Determine which alert to show
  const renderAlerts = () => {
    if (!linePrimeMotorType && linePrimeFanType === "Ziehl-Abegg") {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("linePrimeFan.motorRequired")}
        </Alert>
      );
    }
    if (!linePrimeSelectedFan) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("linePrimeFan.fanRequired")}
        </Alert>
      );
    }
    return null;
  };

  const showFanTable = linePrimeFanType !== "EBM Papst" && !!linePrimeMotorType;

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" } }}
      >
        {/* Title */}
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}>
            {t("linePrimeFan.title")}
          </Typography>
        </Box>

        <Box sx={{ mt: 3 }}>
          {/* Fan Type Selector */}
          <Controller
            name="linePrimeFanType"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  id="select-label-line-prime-fan-type"
                  sx={{ mt: 1, color: "#373437" }}
                >
                  {t("linePrimeFan.fanType")}
                </InputLabel>
                <Select
                  {...field}
                  value={field.value || ""}
                  disablePortal
                  MenuProps={{ disableScrollLock: true }}
                  fullWidth
                  sx={{ mt: 1, mb: 1 }}
                  labelId="select-label-line-prime-fan-type"
                  label={t("linePrimeFan.fanType")}
                  error={!!error}
                >
                  {FAN_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                      {t(option.label)}{option.disabled && <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.disabled" }}>(Coming soon)</Typography>}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* Coming soon notice — shown when EBM Papst is selected */}
          {linePrimeFanType === "EBM Papst" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Coming soon
            </Alert>
          )}

          {/* Motor Type Selector — shown only when Ziehl-Abegg is selected */}
          {linePrimeFanType === "Ziehl-Abegg" && (
            <Box sx={{ mt: 3 }}>
              <Controller
                name="linePrimeMotorType"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <InputLabel
                      id="select-label-line-prime-motor-type"
                      sx={{ mt: 1, color: "#373437" }}
                    >
                      {t("linePrimeFan.motorType")}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || ""}
                      disablePortal
                      MenuProps={{ disableScrollLock: true }}
                      fullWidth
                      sx={{ mt: 1, mb: 1 }}
                      labelId="select-label-line-prime-motor-type"
                      label={t("linePrimeFan.motorType")}
                      error={!!error}
                    >
                      {MOTOR_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          )}
          {/* Fan Table */}
          {showFanTable && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
                {t("linePrimeFan.selectFan")}
              </Typography>

              {linePrimeFanCalculating ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : fanRows.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t("linePrimeFan.noFansLoaded")}
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  <Table size="small">
                    <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          {visibleColumns.map((col) => (
                            <TableCell key={col.label} sx={{ fontWeight: "bold" }}>
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                    <TableBody>
                      <Controller
                        name="linePrimeSelectedFan"
                        control={control}
                        render={({ field }) => (
                          <>
                            {fanRows.map((fan, index) => {
                              const fanId = fan.id ?? index;
                              const fanSignature = getFanSignature(fan);
                              const isSelected =
                                field.value != null &&
                                (field.value === fanId ||
                                  field.value?.id === fanId ||
                                  getFanSignature(field.value) === fanSignature);

                              return (
                                <TableRow
                                  key={fanId}
                                  hover
                                  selected={isSelected}
                                  onClick={() => handleFanSelect(fan, field.onChange)}
                                  sx={{ cursor: "pointer" }}
                                >
                                  <TableCell padding="checkbox">
                                    <Radio
                                      checked={isSelected}
                                      onChange={() => handleFanSelect(fan, field.onChange)}
                                      value={fanId}
                                    />
                                  </TableCell>
                                  {visibleColumns.map((col) => (
                                    <TableCell key={col.label}>
                                      {getFanColumnValue(fan, col)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </>
                        )}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Alerts */}
          {renderAlerts()}

          {/* PNG Preview */}
          {hasFanPreview && (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t("linePrimeFan.fanPreview")}
              </Typography>
              {fanImageSrc ? (
                <Box
                  component="img"
                  src={fanImageSrc}
                  alt={t("linePrimeFan.fanPreview")}
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t("linePrimeFan.fanChartNotFound")}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default LinePrimeFan;
